<script lang="ts" module>
  export type SwarmStats = {
    scene: number;
    kind: string;
    executed: number;
    runs: number;
    held: number;
    strain: number;
    cycle: number;
  };
</script>

<script lang="ts">
  import { onMount } from 'svelte';

  // A fixed, full-viewport particle swarm that re-forms into the idea of
  // whichever [data-swarm] section is nearest the viewport centre.
  //
  //   data-swarm="mandate"  agents working freely inside a boundary; anything
  //                         pushed or drifting out is held (click = pressure)
  //   data-swarm="vitals"   a live vital-signs trace against the agent's own
  //                         normal; cursor proximity raises strain
  //   data-swarm="loops"    two speeds: fast real-time ring, slow flywheel
  //   data-swarm="chart"    polylines from data-swarm-chart JSON
  //   data-swarm="glyphs"   SVG paths from [data-swarm-glyph="<d>"] children
  //   data-swarm="text"     values from data-swarm-text="a|b|c", cycled
  //   data-swarm="orbit"    hub, rings and four satellites
  //   data-swarm="futures"  one pending action fanning into many simulated
  //                         outcomes; a few end badly (red)
  //   data-swarm="paths"    routes from data-swarm-paths JSON: flow stops at
  //                         a closed route's barrier, turns red past a tripwire
  //
  // Optional: data-swarm-label (hold label), data-swarm-caption (boundary
  // caption) for the mandate and vitals scenes.
  //
  // Targets live in [data-swarm-anchor] (or the section itself).

  let { onstats }: { onstats?: (s: SwarmStats) => void } = $props();
  let canvas: HTMLCanvasElement;

  onMount(() => {
    const ctx = canvas.getContext('2d')!;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const small = innerWidth < 760;
    const N = small ? 1100 : 2400;

    const x = new Float32Array(N), y = new Float32Array(N);
    const vx = new Float32Array(N), vy = new Float32Array(N);
    const tx = new Float32Array(N), ty = new Float32Array(N);
    const seed = new Float32Array(N);
    const amber = new Uint8Array(N);
    const hu = new Float32Array(N), hv = new Float32Array(N), hdu = new Float32Array(N), hdv = new Float32Array(N);
    const inside = new Uint8Array(N);
    const drift = new Uint8Array(N);
    const heldAt = new Float32Array(N).fill(-1e9);
    for (let i = 0; i < N; i++) {
      seed[i] = Math.random();
      amber[i] = Math.random() < 0.32 ? 1 : 0;
      x[i] = Math.random() * innerWidth;
      y[i] = scrollY + Math.random() * innerHeight;
      hu[i] = Math.random(); hv[i] = Math.random();
      const a = Math.random() * Math.PI * 2, s = 0.0006 + Math.random() * 0.0014;
      hdu[i] = Math.cos(a) * s; hdv[i] = Math.sin(a) * s;
    }

    type Box = { x: number; y: number; w: number; h: number };
    type Scene = {
      el: HTMLElement; kind: string; box: Box; avoid?: Box;
      glyphs?: { box: Box; pts: number[] }[];
      texts?: string[]; textPts?: number[];
      series?: number[][]; // resampled polylines in doc coords
      label?: string; caption?: string;
      futures?: { pts: number[]; fail: boolean }[];
      routes?: { pts: number[]; status: 'closed' | 'tripwire'; at: number }[];
    };
    let scenes: Scene[] = [];
    let cur = 0, cycle = 0, strain = 0, strainT = 0, lastHold = -1e9;
    let executed = 0, held = 0, runs = 0;
    const tint = new Uint8Array(N);
    let dpr = 1, W = 0, H = 0;

    const docBox = (el: Element): Box => {
      const r = el.getBoundingClientRect();
      return { x: r.left + scrollX, y: r.top + scrollY, w: r.width, h: r.height };
    };

    function sample(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void, step: number) {
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h));
      const g = c.getContext('2d', { willReadFrequently: true })!;
      draw(g);
      const d = g.getImageData(0, 0, c.width, c.height).data;
      const out: number[] = [];
      for (let py = 0; py < c.height; py += step)
        for (let px = 0; px < c.width; px += step) if (d[(py * c.width + px) * 4 + 3] > 128) out.push(px, py);
      for (let i = out.length / 2 - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [out[i * 2], out[j * 2]] = [out[j * 2], out[i * 2]];
        [out[i * 2 + 1], out[j * 2 + 1]] = [out[j * 2 + 1], out[i * 2 + 1]];
      }
      return out;
    }

    function sampleText(s: Scene) {
      const b = s.box, text = s.texts![cycle % s.texts!.length];
      s.textPts = sample(b.w, b.h, (g) => {
        let fs = b.h * 0.9;
        const font = (n: number) => `800 ${n}px 'Manrope Variable', Manrope, sans-serif`;
        g.font = font(fs);
        const m = g.measureText(text).width;
        if (m > b.w * 0.96) fs *= (b.w * 0.96) / m;
        g.font = font(fs);
        g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(text, b.w / 2, b.h / 2 + fs * 0.04);
      }, small ? 4 : 5);
    }

    function rng(seed: number) {
      return () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
    }

    function resample(pts: number[][], M: number) {
      const seg: number[] = [0];
      for (let i = 1; i < pts.length; i++) seg.push(seg[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
      const L = seg[seg.length - 1] || 1, out: number[] = [];
      let j = 0;
      for (let k = 0; k < M; k++) {
        const d = (k / (M - 1)) * L;
        while (j < seg.length - 2 && seg[j + 1] < d) j++;
        const f = (d - seg[j]) / (seg[j + 1] - seg[j] || 1);
        out.push(pts[j][0] + (pts[j + 1][0] - pts[j][0]) * f, pts[j][1] + (pts[j + 1][1] - pts[j][1]) * f);
      }
      return out;
    }

    function layout() {
      dpr = Math.min(devicePixelRatio || 1, 2);
      W = innerWidth; H = innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      scenes = [...document.querySelectorAll<HTMLElement>('[data-swarm]')].map((el) => {
        const anchor = el.querySelector('[data-swarm-anchor]') ?? el;
        const s: Scene = { el, kind: el.dataset.swarm!, box: docBox(anchor) };
        s.label = el.dataset.swarmLabel; s.caption = el.dataset.swarmCaption;
        const av = el.querySelector('[data-swarm-avoid]');
        if (av) s.avoid = docBox(av);
        if (s.kind === 'glyphs') {
          s.glyphs = [...el.querySelectorAll<HTMLElement>('[data-swarm-glyph]')].map((g) => {
            const b = docBox(g), d = g.dataset.swarmGlyph!;
            return {
              box: b,
              pts: sample(b.w, b.h, (c) => {
                const k = (Math.min(b.w, b.h) / 24) * 0.92;
                c.translate(b.w / 2 - 12 * k, b.h / 2 - 12 * k); c.scale(k, k);
                c.lineWidth = 1.7; c.lineCap = 'round'; c.lineJoin = 'round';
                c.stroke(new Path2D(d));
              }, 3)
            };
          });
        } else if (s.kind === 'text') {
          s.texts = (el.dataset.swarmText ?? '').split('|');
          sampleText(s);
        } else if (s.kind === 'chart') {
          const spec = JSON.parse(el.dataset.swarmChart ?? '{}') as { x: [number, number]; series: { y: [number, number]; pts: [number, number][] }[] };
          const b = s.box;
          s.series = spec.series.map((sr) =>
            resample(sr.pts.map(([px, py]) => [
              b.x + ((px - spec.x[0]) / (spec.x[1] - spec.x[0])) * b.w,
              b.y + b.h - ((py - sr.y[0]) / (sr.y[1] - sr.y[0])) * b.h
            ]), 600)
          );
        } else if (s.kind === 'futures') {
          const b = s.box, K = small ? 70 : 140, M = 60, rnd = rng(11);
          s.futures = Array.from({ length: K }, (_, k) => {
            const end = (k / (K - 1) - 0.5) * 0.92 + (rnd() - 0.5) * 0.04;
            const fail = rnd() < 0.09, ph = rnd() * 6.28, fr = 2 + rnd() * 3, dir = end >= 0 ? 1 : -1;
            const pts: number[] = [];
            for (let j = 0; j < M; j++) {
              const u = j / (M - 1), ease = u * u * (3 - 2 * u);
              let yy = end * ease + Math.sin(u * fr * Math.PI + ph) * 0.035 * u;
              if (fail && u > 0.72) yy += dir * 0.22 * ((u - 0.72) / 0.28) ** 2;
              pts.push(b.x + u * b.w, b.y + b.h / 2 + yy * b.h);
            }
            return { pts, fail };
          });
        } else if (s.kind === 'paths') {
          const b = s.box;
          const spec = JSON.parse(el.dataset.swarmPaths ?? '{}') as { nodes: Record<string, [number, number]>; routes: { via: string[]; status: 'closed' | 'tripwire'; at: number | [number, number] }[] };
          s.routes = spec.routes.map((r) => {
            const poly = r.via.map((id) => [b.x + spec.nodes[id][0] * b.w, b.y + spec.nodes[id][1] * b.h]);
            // `at` is a fraction of the whole route, or [segment, fraction of that segment]
            let at = typeof r.at === 'number' ? r.at : 0;
            if (Array.isArray(r.at)) {
              const len = poly.slice(1).map((q, k) => Math.hypot(q[0] - poly[k][0], q[1] - poly[k][1]));
              const total = len.reduce((a, c) => a + c, 0) || 1;
              at = (len.slice(0, r.at[0]).reduce((a, c) => a + c, 0) + r.at[1] * len[r.at[0]]) / total;
            }
            return { status: r.status, at, pts: resample(poly, 240) };
          });
        }
        return s;
      });
      pickScene();
    }

    // ---------- pointer ----------
    let lastPush = -1e9;
    let mx = -9999, my = -9999, pointerOn = false, pSpeed = 0, lmx = 0, lmy = 0;
    type Burst = { x: number; y: number; t: number; label: string };
    let bursts: Burst[] = [];
    const onMove = (e: PointerEvent) => {
      mx = e.clientX + scrollX; my = e.clientY + scrollY; pointerOn = true;
    };
    const onLeave = () => { pointerOn = false; };
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('a,button,input,textarea,label,select,video,.azimuth-live')) return;
      const px = e.clientX + scrollX, py = e.clientY + scrollY;
      for (let i = 0; i < N; i++) {
        const dx = x[i] - px, dy = y[i] - py, d = Math.hypot(dx, dy) || 1;
        if (d < 170) { const f = ((170 - d) / 170) * 16; vx[i] += (dx / d) * f; vy[i] += (dy / d) * f; }
      }
      if (scenes[cur]?.kind === 'vitals') strainT = Math.min(1, strainT + 0.5);
      bursts.push({ x: px, y: py, t: performance.now(), label: 'PRESSURE' });
      lastPush = performance.now();
    };
    addEventListener('pointermove', onMove, { passive: true });
    addEventListener('pointerdown', onDown);
    document.documentElement.addEventListener('pointerleave', onLeave);

    function pickScene() {
      const mid = scrollY + innerHeight * 0.5;
      let best = cur, bd = Infinity;
      scenes.forEach((s, k) => {
        const b = docBox(s.el);
        const d = mid < b.y ? b.y - mid : mid > b.y + b.h ? mid - (b.y + b.h) : 0;
        if (d < bd) { bd = d; best = k; }
      });
      if (best !== cur) {
        cur = best;
        inside.fill(0); drift.fill(0); tint.fill(0);
        for (let i = 0; i < N; i++) { vx[i] += (Math.random() - 0.5) * 6; vy[i] += (Math.random() - 0.5) * 6; }
      }
    }
    addEventListener('scroll', pickScene, { passive: true });

    const cycler = setInterval(() => {
      const s = scenes[cur];
      if (!s || s.kind !== 'text' || !s.texts || s.texts.length < 2) return;
      cycle++;
      sampleText(s);
      for (let i = 0; i < N; i++) { vx[i] += (Math.random() - 0.5) * 9; vy[i] -= Math.random() * 4; }
    }, 2800);

    let layoutTimer: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver(() => { clearTimeout(layoutTimer); layoutTimer = setTimeout(layout, 120); });
    ro.observe(document.body);
    document.fonts?.ready.then(layout);
    layout();

    const PAD = 18;
    const outOf = (b: Box, px: number, py: number, m: number) => px < b.x - m || px > b.x + b.w + m || py < b.y - m || py > b.y + b.h + m;

    // ECG-like beat, p in [0,1)
    const beat = (p: number) =>
      0.12 * Math.exp(-((p - 0.12) ** 2) / 0.0012) -
      0.22 * Math.exp(-((p - 0.28) ** 2) / 0.0004) +
      1.0 * Math.exp(-((p - 0.31) ** 2) / 0.0007) -
      0.34 * Math.exp(-((p - 0.345) ** 2) / 0.0005) +
      0.22 * Math.exp(-((p - 0.56) ** 2) / 0.0035);

    function targets(t: number) {
      const s = scenes[cur];
      if (!s) return;
      const b = s.box;
      if (s.kind === 'mandate') {
        for (let i = 0; i < N; i++) {
          hu[i] += hdu[i]; hv[i] += hdv[i];
          if (hu[i] < 0 || hu[i] > 1) { hdu[i] *= -1; hu[i] = Math.max(0, Math.min(1, hu[i])); }
          if (hv[i] < 0 || hv[i] > 1) { hdv[i] *= -1; hv[i] = Math.max(0, Math.min(1, hv[i])); }
          let cx = b.x + PAD + hu[i] * (b.w - PAD * 2), cy = b.y + PAD + hv[i] * (b.h - PAD * 2);
          const av = s.avoid;
          if (av && cx > av.x && cx < av.x + av.w && cy > av.y && cy < av.y + av.h) {
            // keep the copy clear: bounce homes off the protected rectangle
            const dl = cx - av.x, dr = av.x + av.w - cx, dt = cy - av.y, db = av.y + av.h - cy, m = Math.min(dl, dr, dt, db);
            if (m === dl) { cx = av.x; hdu[i] = -Math.abs(hdu[i]); } else if (m === dr) { cx = av.x + av.w; hdu[i] = Math.abs(hdu[i]); }
            else if (m === dt) { cy = av.y; hdv[i] = -Math.abs(hdv[i]); } else { cy = av.y + av.h; hdv[i] = Math.abs(hdv[i]); }
            hu[i] = (cx - b.x - PAD) / (b.w - PAD * 2); hv[i] = (cy - b.y - PAD) / (b.h - PAD * 2);
          }
          if (drift[i]) {
            const ox = cx - (b.x + b.w / 2), oy = cy - (b.y + b.h / 2), od = Math.hypot(ox, oy) || 1;
            tx[i] = cx + (ox / od) * 400; ty[i] = cy + (oy / od) * 400;
          } else { tx[i] = cx; ty[i] = cy; }
        }
        if (Math.random() < (small ? 0.012 : 0.02)) drift[(Math.random() * N) | 0] = 1;
      } else if (s.kind === 'vitals') {
        const mid = b.y + b.h * 0.62, amp = b.h * (0.5 + 0.3 * strain);
        const period = b.w / (small ? 2.2 : 3.4) / (1 + strain * 0.9);
        const phase = t * 0.00018 * (1 + strain * 1.6);
        for (let i = 0; i < N; i++) {
          const u = (i * 0.618034) % 1, px = b.x + u * b.w;
          if (amber[i]) {
            // the agent's own normal: calm baseline beat, thin band
            const p = (((px - b.x) / (b.w / (small ? 2.2 : 3.4)) - t * 0.00018) % 1 + 1) % 1;
            tx[i] = px; ty[i] = mid - beat(p) * b.h * 0.5 + (seed[i] - 0.5) * 3;
          } else {
            const p = (((px - b.x) / period - phase) % 1 + 1) % 1;
            const noise = strain * (Math.sin(px * 0.05 + t * 0.01) * 0.12 + (seed[i] - 0.5) * 0.25);
            const narrowing = strain * 0.18 * Math.sin((px - b.x) / b.w * Math.PI * 2 + t * 0.002);
            tx[i] = px; ty[i] = mid - (beat(p) + noise + narrowing) * amp + (seed[i] - 0.5) * 4;
          }
        }
      } else if (s.kind === 'futures' && s.futures) {
        const F = s.futures, K = F.length;
        for (let i = 0; i < N; i++) {
          const f = F[i % K], M = f.pts.length / 2;
          const u = (seed[i] * 3.7 + t * 0.00007 * (0.7 + seed[i] * 0.6)) % 1;
          const fj = u * (M - 1), j = Math.floor(fj), q = fj - j, j2 = Math.min(M - 1, j + 1);
          tx[i] = f.pts[j * 2] + (f.pts[j2 * 2] - f.pts[j * 2]) * q;
          ty[i] = f.pts[j * 2 + 1] + (f.pts[j2 * 2 + 1] - f.pts[j * 2 + 1]) * q;
          tint[i] = f.fail && u > 0.72 ? 1 : 0;
        }
      } else if (s.kind === 'paths' && s.routes?.length) {
        const Rt = s.routes, Rn = Rt.length;
        for (let i = 0; i < N; i++) {
          const r = Rt[i % Rn], M = r.pts.length / 2;
          let u = (seed[i] * 5.3 + t * 0.0001 * (0.7 + seed[i] * 0.6)) % 1;
          if (r.status === 'closed') u *= r.at; // flow piles up at the barrier
          const j = Math.min(M - 1, Math.floor(u * (M - 1)));
          tx[i] = r.pts[j * 2] + (seed[i] - 0.5) * 5; ty[i] = r.pts[j * 2 + 1] + (((i * 31) % 17) / 17 - 0.5) * 5;
          tint[i] = r.status === 'tripwire' && u > r.at ? 1 : 0;
        }
      } else if (s.kind === 'loops') {
        const cx = b.x + b.w / 2, cy = b.y + b.h / 2, R = Math.min(b.w, b.h) / 2;
        for (let i = 0; i < N; i++) {
          let r: number, a: number;
          if (amber[i]) { r = R * (0.3 + (seed[i] - 0.5) * 0.05); a = i * 0.37 + t * 0.0022; }
          else if (seed[i] < 0.85) { r = R * (0.9 + (seed[i] - 0.4) * 0.06); a = i * 0.61 - t * 0.00012; }
          else {
            // fixes flowing inward from the flywheel to the real-time loop
            const q = ((t * 0.00008 + seed[i] * 7) % 1);
            r = R * (0.9 - q * 0.6); a = i * 1.7 + q * 3.2 - t * 0.0002;
          }
          tx[i] = cx + Math.cos(a) * r; ty[i] = cy + Math.sin(a) * r;
        }
      } else if (s.kind === 'chart' && s.series) {
        const [s0, s1] = s.series;
        for (let i = 0; i < N; i++) {
          const ser = amber[i] && s1 ? s1 : s0, M = ser.length / 2;
          const j = (((i * 7919) % M) | 0) * 2;
          tx[i] = ser[j] + (seed[i] - 0.5) * 3; ty[i] = ser[j + 1] + (((i * 31) % 17) / 17 - 0.5) * 5;
        }
      } else if (s.kind === 'glyphs' && s.glyphs?.length) {
        const G = s.glyphs.length, per = Math.ceil(N / G);
        for (let i = 0; i < N; i++) {
          const g = s.glyphs[Math.min(G - 1, (i / per) | 0)];
          if (!g.pts.length) continue;
          const j = ((i % (g.pts.length / 2)) | 0) * 2, jit = Math.sin(t * 0.002 + seed[i] * 40) * 1.4;
          tx[i] = g.box.x + g.pts[j] + jit; ty[i] = g.box.y + g.pts[j + 1] + jit;
        }
      } else if (s.kind === 'text' && s.textPts?.length) {
        const M = s.textPts.length / 2;
        for (let i = 0; i < N; i++) {
          const j = (i % M) * 2;
          tx[i] = b.x + s.textPts[j] + (seed[i] - 0.5) * 4; ty[i] = b.y + s.textPts[j + 1] + (((i * 7919) % 97) / 97 - 0.5) * 4;
        }
      } else if (s.kind === 'orbit') {
        const cx = b.x + b.w / 2, cy = b.y + b.h / 2, R = Math.min(b.w, b.h) / 2;
        for (let i = 0; i < N; i++) {
          const q = seed[i];
          if (q < 0.2) { const r = Math.sqrt(q / 0.2) * R * 0.2, a = i * 2.399 + t * 0.0003; tx[i] = cx + Math.cos(a) * r; ty[i] = cy + Math.sin(a) * r; }
          else if (q < 0.72) {
            const ring = q < 0.38 ? 0.46 : q < 0.55 ? 0.68 : 0.9, a = i * 0.7 + t * 0.00018 * (ring === 0.68 ? -1 : 1);
            tx[i] = cx + Math.cos(a) * (R * ring + Math.sin(i) * 2); ty[i] = cy + Math.sin(a) * (R * ring + Math.sin(i) * 2);
          } else {
            const na = (i % 4) * (Math.PI / 2) - Math.PI / 4 - t * 0.00018, nr = Math.sqrt((q - 0.72) / 0.28) * R * 0.11;
            tx[i] = cx + Math.cos(na) * R * 0.68 + Math.cos(i * 2.399) * nr; ty[i] = cy + Math.sin(na) * R * 0.68 + Math.sin(i * 2.399) * nr;
          }
        }
      }
    }

    let raf = 0, lastStats = 0;
    const INK = '#111314', AMB = '#E5A355', RED = '#d6464b';
    function frame(t: number) {
      const s = scenes[cur];
      const kind = s?.kind ?? '';
      // strain (vitals scene): cursor closeness to the trace + pointer speed
      pSpeed = pSpeed * 0.9 + Math.hypot(mx - lmx, my - lmy) * 0.1; lmx = mx; lmy = my;
      if (kind === 'vitals' && s) {
        const b = s.box;
        const near = pointerOn && !outOf(b, mx, my, 60) ? 1 - Math.min(1, Math.abs(my - (b.y + b.h / 2)) / (b.h / 2 + 60)) : 0;
        const auto = pointerOn ? 0 : 0.45 + 0.45 * Math.sin(t * 0.0005);
        strainT = Math.max(strainT * 0.985, near * 0.7 + Math.min(0.4, pSpeed / 60), auto);
        strain += (Math.min(1, strainT) - strain) * 0.05;
        if (strain > 0.78 && t - lastHold > 2200) {
          lastHold = t; held++;
          bursts.push({ x: b.x + b.w * 0.82, y: b.y + b.h * 0.2, t, label: s.label ?? 'HOLD · STRAIN ABOVE ITS OWN NORMAL' });
          strainT = 0.2; // relieve the pressure
        }
      } else { strain *= 0.95; strainT *= 0.95; }

      targets(t);

      for (let i = 0; i < N; i++) {
        let k = reduce ? 1 : 0.018 + seed[i] * 0.03;
        let ax = 0, ay = 0;
        if (pointerOn) {
          const dx = x[i] - mx, dy = y[i] - my, d = Math.hypot(dx, dy) || 1;
          if (d < 110) { const f = ((110 - d) / 110) * 2.2; ax += (dx / d) * f; ay += (dy / d) * f; k *= 0.4; }
        }
        ax += (tx[i] - x[i]) * k; ay += (ty[i] - y[i]) * k;
        if (reduce) { x[i] = tx[i]; y[i] = ty[i]; } else {
          vx[i] = (vx[i] + ax) * 0.84; vy[i] = (vy[i] + ay) * 0.84;
          x[i] += vx[i]; y[i] += vy[i];
        }
        if (kind === 'mandate' && s) {
          const out = outOf(s.box, x[i], y[i], 10);
          if (!out) inside[i] = 1;
          else if (inside[i] && !drift[i] && !(pointerOn && Math.hypot(x[i] - mx, y[i] - my) < 160) && !(t - lastPush < 900)) {
            inside[i] = 0; // settling overshoot: not a real exit
          } else if (inside[i]) {
            // crossed the mandate boundary: held, flagged, returned inside
            inside[i] = 0; drift[i] = 0; heldAt[i] = t; held++;
            vx[i] *= -0.4; vy[i] *= -0.4;
            if (bursts.length < 12) bursts.push({ x: x[i], y: y[i], t, label: s.label ?? 'HELD · OUT OF MANDATE' });
          }
        }
      }
      if (kind === 'mandate') executed += small ? 2 : 4;
      if (kind === 'futures') runs = Math.min(10000, runs + 29);

      // ---- draw ----
      const sy = scrollY, sx = scrollX;
      ctx.setTransform(dpr, 0, 0, dpr, -sx * dpr, -sy * dpr);
      ctx.clearRect(sx, sy, W, H);
      const top = sy - 10, bot = sy + H + 10;

      if (kind === 'mandate' && s) {
        const b = s.box;
        ctx.strokeStyle = INK; ctx.globalAlpha = 0.35; ctx.lineWidth = 1; ctx.setLineDash([6, 6]);
        ctx.strokeRect(b.x - 6, b.y - 6, b.w + 12, b.h + 12);
        ctx.setLineDash([]); ctx.globalAlpha = 0.7;
        ctx.font = "600 10px 'JetBrains Mono', monospace"; ctx.fillStyle = INK;
        ctx.fillText(s.caption ?? 'MANDATE · SET BY YOUR TEAM', b.x - 6, b.y - 14);
        ctx.globalAlpha = 1;
      }
      if (kind === 'vitals' && s) {
        const b = s.box;
        ctx.globalAlpha = 0.5; ctx.font = "600 10px 'JetBrains Mono', monospace";
        ctx.fillStyle = '#94653E'; ctx.fillText("● ITS OWN NORMAL", b.x, b.y + b.h + 18);
        ctx.fillStyle = strain > 0.5 ? RED : INK; ctx.fillText(`● LIVE · STRAIN ${strain.toFixed(2)}`, b.x + 150, b.y + b.h + 18);
        ctx.globalAlpha = 1;
      }

      if (kind === 'futures' && s?.futures) {
        const b = s.box, ox = b.x, oy = b.y + b.h / 2;
        ctx.strokeStyle = AMB; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(ox, oy, 9 + Math.sin(t * 0.004) * 2, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(ox, oy, 4, 0, Math.PI * 2); ctx.fill();
        ctx.font = "600 10px 'JetBrains Mono', monospace"; ctx.globalAlpha = 0.75;
        ctx.fillText('PAYMENT · NOT YET SENT', ox - 4, oy - 22);
        ctx.fillText(`${runs.toLocaleString()} FUTURES SIMULATED`, b.x + b.w - 190, b.y - 10);
        ctx.globalAlpha = 1;
      }
      if (kind === 'paths' && s?.routes) {
        ctx.font = "600 9px 'JetBrains Mono', monospace";
        const labelled = new Set<string>();
        const tag = (text: string, px: number, py: number) => {
          const key = `${text}${Math.round(px / 40)}:${Math.round(py / 40)}`;
          if (text === 'TRIPWIRE' ? labelled.has(text) : labelled.has(key)) return;
          labelled.add(key); labelled.add(text === 'TRIPWIRE' ? text : key);
          ctx.fillText(text, px + 9, py - 10);
        };
        for (const r of s.routes) {
          const M = r.pts.length / 2;
          ctx.strokeStyle = INK; ctx.globalAlpha = 0.1; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(r.pts[0], r.pts[1]);
          for (let j = 1; j < M; j++) ctx.lineTo(r.pts[j * 2], r.pts[j * 2 + 1]);
          ctx.stroke(); ctx.globalAlpha = 1;
          const j = Math.min(M - 2, Math.floor(r.at * (M - 1)));
          const px = r.pts[j * 2], py = r.pts[j * 2 + 1];
          const ang = Math.atan2(r.pts[j * 2 + 3] - py, r.pts[j * 2 + 2] - px) + Math.PI / 2;
          if (r.status === 'closed') {
            ctx.strokeStyle = INK; ctx.lineWidth = 3;
            ctx.beginPath(); ctx.moveTo(px - Math.cos(ang) * 10, py - Math.sin(ang) * 10); ctx.lineTo(px + Math.cos(ang) * 10, py + Math.sin(ang) * 10); ctx.stroke();
            ctx.fillStyle = INK; tag('CLOSED', px, py);
          } else {
            ctx.fillStyle = RED; ctx.save(); ctx.translate(px, py); ctx.rotate(Math.PI / 4); ctx.fillRect(-5, -5, 10, 10); ctx.restore();
            tag('TRIPWIRE', px, py);
          }
        }
      }

      const inkColor = kind === 'vitals' && strain > 0.55 ? RED : INK;
      ctx.fillStyle = inkColor; ctx.globalAlpha = 0.8;
      for (let i = 0; i < N; i++) if (!amber[i] && !tint[i] && y[i] > top && y[i] < bot && t - heldAt[i] > 1400) ctx.fillRect(x[i], y[i], 1.7, 1.7);
      ctx.globalAlpha = 1; ctx.fillStyle = AMB;
      for (let i = 0; i < N; i++) if (amber[i] && !tint[i] && y[i] > top && y[i] < bot && t - heldAt[i] > 1400) ctx.fillRect(x[i] - 0.4, y[i] - 0.4, 2.6, 2.6);
      ctx.fillStyle = RED;
      for (let i = 0; i < N; i++) if (t - heldAt[i] <= 1400) ctx.fillRect(x[i] - 1, y[i] - 1, 3.4, 3.4);
      for (let i = 0; i < N; i++) if (tint[i] && y[i] > top && y[i] < bot) ctx.fillRect(x[i] - 0.5, y[i] - 0.5, 2.4, 2.4);

      bursts = bursts.filter((u) => t - u.t < 900);
      for (const u of bursts) {
        const q = (t - u.t) / 900;
        ctx.globalAlpha = 1 - q; ctx.lineWidth = 1.5;
        ctx.strokeStyle = u.label === 'PRESSURE' ? AMB : RED;
        ctx.beginPath(); ctx.arc(u.x, u.y, 5 + q * (u.label === 'PRESSURE' ? 120 : 30), 0, Math.PI * 2); ctx.stroke();
        if (u.label !== 'PRESSURE') {
          ctx.font = "600 10px 'JetBrains Mono', monospace"; ctx.fillStyle = RED;
          ctx.fillText(u.label, u.x + 10, u.y - 10 - q * 12);
        }
        ctx.globalAlpha = 1;
      }

      if (onstats && t - lastStats > 120) {
        lastStats = t;
        onstats({ scene: cur, kind, executed, runs, held, strain, cycle: s?.texts ? cycle % s.texts.length : 0 });
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf); clearInterval(cycler); clearTimeout(layoutTimer); ro.disconnect();
      removeEventListener('pointermove', onMove); removeEventListener('pointerdown', onDown);
      removeEventListener('scroll', pickScene);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  });
</script>

<canvas bind:this={canvas} class="swarm" aria-hidden="true"></canvas>

<style>
  .swarm { position: fixed; inset: 0; width: 100vw; height: 100vh; pointer-events: none; z-index: 0; }
</style>
